import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import {
  MdClose,
  MdChevronRight,
  MdOutlineBusinessCenter,
  MdOutlineSupportAgent,
  MdPersonOutline,
  MdArrowBack,
  MdOutlineSettings,
} from "react-icons/md";
import { TbLogout } from "react-icons/tb";
import "./mobileNav.css";
import { searchItems } from "../NavV2/navModules";

const CLOSE_MS = 220;
const SUPPORT_EMAIL = "sales@ezeitpro.com";

const FOCUSABLE =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The phone "Menu": a bottom sheet holding the account card and every
 * destination, grouped exactly as the desktop sidebar groups them.
 *
 * Replaces the old flat "More" popover (180px wide, no scroll, no groups) that
 * clipped off-screen with more than a handful of items.
 */
const MenuSheet = ({ open, onClose, navGroups, v2 = null, account, actionsRef }) => {
  const location = useLocation();
  const sheetRef = useRef(null);
  const closeRef = useRef(null);
  const returnFocusRef = useRef(null);
  const drag = useRef({ active: false, startY: 0, dy: 0 });
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  // New menu: which module is drilled into, and the search text. Both reset when the sheet closes.
  const [drill, setDrill] = useState(null);
  const [query, setQuery] = useState("");
  useEffect(() => {
    if (!open) {
      setDrill(null);
      setQuery("");
    }
  }, [open]);

  // Mount, then flip to "shown" a frame later so the slide-in transition runs;
  // on close, slide out first and unmount after it finishes.
  useEffect(() => {
    if (open) {
      returnFocusRef.current = document.activeElement;
      setMounted(true);
      const raf = window.requestAnimationFrame(() => setShown(true));
      return () => window.cancelAnimationFrame(raf);
    }
    setShown(false);
    const t = window.setTimeout(() => setMounted(false), CLOSE_MS);
    return () => window.clearTimeout(t);
  }, [open]);

  // Lock page scroll behind the sheet; hand focus in and back out.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 30);
    return () => {
      document.body.style.overflow = previous;
      window.clearTimeout(focusTimer);
      const el = returnFocusRef.current;
      if (el && typeof el.focus === "function") el.focus();
    };
  }, [open]);

  // Close on navigation (a tile tap, browser back, or the header bell).
  const firstPath = useRef(location.pathname);
  useEffect(() => {
    if (firstPath.current !== location.pathname) {
      firstPath.current = location.pathname;
      if (open) onClose();
    }
  }, [location.pathname, open, onClose]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !sheetRef.current) return;
      const nodes = Array.from(sheetRef.current.querySelectorAll(FOCUSABLE));
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  // Swipe down on the grabber to dismiss.
  const onGrabStart = (e) => {
    drag.current = { active: true, startY: e.clientY, dy: 0 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    sheetRef.current?.classList.add("is-dragging");
  };
  const onGrabMove = (e) => {
    if (!drag.current.active) return;
    const dy = Math.max(0, e.clientY - drag.current.startY);
    drag.current.dy = dy;
    if (sheetRef.current) sheetRef.current.style.transform = `translateY(${dy}px)`;
  };
  const onGrabEnd = () => {
    if (!drag.current.active) return;
    const { dy } = drag.current;
    drag.current.active = false;
    const sheet = sheetRef.current;
    if (sheet) {
      sheet.classList.remove("is-dragging");
      sheet.style.transform = "";
    }
    if (dy > 90) onClose();
  };

  if (!mounted) return null;

  const run = (name) => () => {
    onClose();
    actionsRef?.current?.[name]?.();
  };

  const initial = (account?.name || "").trim().charAt(0).toUpperCase() || "E";
  const links = navGroups.filter((g) => g.type === "link");
  const groups = navGroups.filter((g) => g.type === "group");

  // "Items" (/inventory) is a path prefix of its siblings; match it exactly so
  // it is not highlighted on every other inventory page.
  const allItems = [...links, ...groups.flatMap((g) => g.items)];
  const needsExactMatch = (item) =>
    allItems.some((other) => other !== item && other.to.startsWith(`${item.to}/`));

  const renderTile = (item) => {
    const Icon = item.icon;
    return (
      <NavLink
        key={item.key}
        to={item.to}
        end={needsExactMatch(item)}
        className={({ isActive }) => `mnav-tile${isActive ? " is-active" : ""}`}
        onClick={onClose}
      >
        <span className="mnav-tile-icon">
          <Icon size={24} />
        </span>
        <span className="mnav-tile-label">{item.label}</span>
      </NavLink>
    );
  };

  const renderV2 = () => {
    if (query.trim()) {
      const hits = searchItems(v2.items, query);
      return hits.length ? <div className="mnav-grid">{hits.map(renderTile)}</div> : <div className="mnav-empty">No page matches that.</div>;
    }
    const mod = drill && v2.modules.find((m) => m.id === drill);
    if (mod) {
      return (
        <section aria-label={mod.label}>
          <button type="button" className="mnav-back" onClick={() => setDrill(null)}>
            <MdArrowBack size={20} /> All modules
          </button>
          <h2 className="mnav-drill-title">{mod.label}</h2>
          {mod.sections.map((sec, i) => (
            <div key={sec.title || `s${i}`}>
              {sec.title ? <h3 className="mnav-group-title">{sec.title}</h3> : <div style={{ height: 10 }} />}
              <div className="mnav-grid">{sec.items.map(renderTile)}</div>
            </div>
          ))}
        </section>
      );
    }
    const pinned = v2.pins.map((k) => v2.items.find((i) => i.key === k)).filter(Boolean);
    return (
      <>
        {pinned.length > 0 && (
          <section aria-label="Pinned">
            <h2 className="mnav-group-title">Pinned</h2>
            <div className="mnav-grid">{pinned.map(renderTile)}</div>
          </section>
        )}
        <section aria-label="Modules">
          <h2 className="mnav-group-title">Modules</h2>
          <div className="mnav-grid">
            {v2.modules.map((m) => {
              if (m.single) return renderTile({ ...m.items[0], icon: m.icon });
              const Icon = m.icon;
              const here = m.items.some((it) => location.pathname === it.to.split("?")[0]);
              return (
                <button key={m.id} type="button" className={`mnav-tile${here ? " is-active" : ""}`} onClick={() => setDrill(m.id)}>
                  <span className="mnav-tile-icon">
                    <Icon size={24} />
                  </span>
                  <span className="mnav-tile-label">{m.label}</span>
                </button>
              );
            })}
            {renderTile({ key: "settings-hub", to: "/settings", label: "Settings", icon: MdOutlineSettings })}
          </div>
        </section>
      </>
    );
  };

  return ReactDOM.createPortal(
    <>
      <div className={`mnav-scrim${shown ? " is-open" : ""}`} onClick={onClose} aria-hidden="true" />
      <div
        ref={sheetRef}
        className={`mnav-sheet${shown ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        onKeyDown={onKeyDown}
      >
        <div
          className="mnav-grabber-zone"
          onPointerDown={onGrabStart}
          onPointerMove={onGrabMove}
          onPointerUp={onGrabEnd}
          onPointerCancel={onGrabEnd}
        >
          <span className="mnav-grabber" />
        </div>

        <div className="mnav-sheet-body">
          <div className="mnav-account">
            <span className="mnav-avatar" aria-hidden="true">
              {account?.logo ? <img src={account.logo} alt="" /> : initial}
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="mnav-account-name">{account?.name || "Menu"}</div>
              {account?.plan && <div className="mnav-account-plan">{account.plan}</div>}
            </div>
            <button ref={closeRef} type="button" className="mnav-close" onClick={onClose} aria-label="Close menu">
              <MdClose size={22} />
            </button>
          </div>

          {account?.setupIncomplete && (
            <button type="button" className="mnav-setup" onClick={run("finishSetup")}>
              <span>Setup incomplete - finish setting up</span>
              <MdChevronRight size={20} />
            </button>
          )}

          <div className="mnav-quick">
            {account?.showOwnerInfo && (
              <button type="button" className="mnav-quick-btn" onClick={run("profile")}>
                <MdPersonOutline size={18} /> Profile
              </button>
            )}
            <button type="button" className="mnav-quick-btn" onClick={run("businessInfo")}>
              <MdOutlineBusinessCenter size={18} /> Business
            </button>
            <a className="mnav-quick-btn" href={`mailto:${SUPPORT_EMAIL}`}>
              <MdOutlineSupportAgent size={18} /> Support
            </a>
          </div>

          {v2 ? (
            <>
              <input
                type="search"
                className="mnav-search"
                placeholder="Search pages"
                aria-label="Search pages"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {renderV2()}
            </>
          ) : (
            <>
              {links.length > 0 && (
                <div className="mnav-grid">
                  {links.map(renderTile)}
                </div>
              )}

              {groups.map((group) => (
                <section key={group.key} aria-label={group.label}>
                  <h2 className="mnav-group-title">{group.label}</h2>
                  <div className="mnav-grid">
                    {group.items.map(renderTile)}
                  </div>
                </section>
              ))}
            </>
          )}

          <button type="button" className="mnav-signout" onClick={run("signOut")}>
            <TbLogout size={20} /> Sign out
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default MenuSheet;
